import React, { useEffect, useMemo, useState } from "react";
import "./Header.css";
import { FaBars, FaTimes, FaPhoneAlt } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLocalContext } from "../../context/LocalContext";
import { resolveSiteName } from "../../utils/siteConfig";

const drawerAnim = {
  hidden: { x: 40, opacity: 0 },
  show: { x: 0, opacity: 1, transition: { duration: 0.28, ease: "easeOut" } },
  exit: { x: 40, opacity: 0, transition: { duration: 0.2 } },
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { webinfo = {} } = useLocalContext();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navItems = useMemo(
    () => [
      { label: "Home", path: "/" },
      { label: "Services", path: "/services" },
      { label: "How it works", path: "/process" },
      { label: "About", path: "/about" },
      { label: "Contact", path: "/contact" },
    ],
    []
  );

  const phoneCall = webinfo?.phonecall || webinfo?.phone || "";
  const phoneText = webinfo?.phone || webinfo?.phonecall || "Call now";
  const brand = resolveSiteName(webinfo);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <header className={`sssHdr ${scrolled ? "sssHdr--scrolled" : ""}`}>
      <div className="sssHdr__row">
        <button type="button" className="sssHdr__brand" onClick={() => go("/")} aria-label={brand}>
          <span className="sssHdr__mark" aria-hidden="true" />
          <span className="sssHdr__name">
            Smart Setup
            <em>Solutions</em>
          </span>
        </button>

        <nav className="sssHdr__nav" aria-label="Main">
          {navItems.map((it) => (
            <button
              key={it.path}
              type="button"
              className={`sssHdr__link ${location.pathname === it.path ? "is-active" : ""}`}
              onClick={() => go(it.path)}
            >
              {it.label}
            </button>
          ))}
        </nav>

        <div className="sssHdr__actions">
          {phoneCall && (
            <a className="sssHdr__call" href={`tel:${phoneCall}`}>
              <FaPhoneAlt />
              <span>{phoneText}</span>
            </a>
          )}
          <button type="button" className="sssHdr__cta" onClick={() => go("/contact")}>
            Get help
          </button>
          <button type="button" className="sssHdr__burger" onClick={() => setOpen(true)} aria-label="Open menu">
            <FaBars />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="sssHdr__overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              className="sssHdr__drawer"
              variants={drawerAnim}
              initial="hidden"
              animate="show"
              exit="exit"
            >
              <div className="sssHdr__drawerTop">
                <span className="sssHdr__name">
                  Smart Setup<em>Solutions</em>
                </span>
                <button type="button" onClick={() => setOpen(false)} aria-label="Close">
                  <FaTimes />
                </button>
              </div>
              {navItems.map((it) => (
                <button key={it.path} type="button" className="sssHdr__mLink" onClick={() => go(it.path)}>
                  {it.label}
                </button>
              ))}
              <button type="button" className="sssHdr__cta sssHdr__cta--block" onClick={() => go("/contact")}>
                Get help now
              </button>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
